import { Receipt, ShieldCheck } from 'lucide-react'

import {
  type DrawerPage,
  type DrawerProduct,
  DrawerProductRows,
  ProductNavDrawer,
  SidebarSwitchRow,
  useProductShell,
} from '@/components/sidebar'
import { bookkeeperHref } from '@/config/config'
import { SidebarTab } from '@/lib/sidebarTab'
import type { User } from '@/models/user'
import { CONSOLE_SWITCH, insuranceDrawerPages, productTabs, SIDEBAR_TAB_LABEL } from './navigation'

const TAB_ICON = {
  [SidebarTab.Insurance]: ShieldCheck,
  [SidebarTab.Compliance]: ShieldCheck,
  [SidebarTab.Finance]: Receipt,
}

interface MobileNavDrawerProps {
  user: User
  activeTab: Exclude<SidebarTab, SidebarTab.Console>
  /** Opens a tab with no pages here, which shows its offer, or switches to the console. */
  onSwitch: (tab: SidebarTab) => void
  onNavigate: () => void
}

/** Insure's phone menu: the shared drawer over the user's own pages, plus the console switch for staff; in the shell, its groups. */
export function MobileNavDrawer({ user, activeTab, onSwitch, onNavigate }: MobileNavDrawerProps) {
  // In the shared shell Bookkeeper is its own tab in the bar, not a group in Insure's menu.
  const shell = useProductShell()
  const tabs = productTabs(user).filter((tab) => !shell || tab !== SidebarTab.Finance)
  const products: DrawerProduct<SidebarTab>[] = tabs.map((tab) => ({
    value: tab,
    label: SIDEBAR_TAB_LABEL[tab],
    icon: TAB_ICON[tab as keyof typeof TAB_ICON],
    pages: tabRows(tab, user),
    // Nothing to list means a product the account doesn't have: the row opens its offer.
    onSelect: () => onSwitch(tab),
  }))

  // The shell's menu holds Cai and the other products; Insure's groups take Insurance's place in it.
  if (shell) return <DrawerProductRows products={products} active={activeTab} onNavigate={onNavigate} />

  return (
    <ProductNavDrawer products={products} active={activeTab} onNavigate={onNavigate}>
      {user.isCaiAdmin && (
        <ul className="border-ink-100 mt-1 border-t pt-1">
          <SidebarSwitchRow
            title={CONSOLE_SWITCH.title}
            icon={CONSOLE_SWITCH.icon}
            onClick={() => onSwitch(CONSOLE_SWITCH.tab)}
          />
        </ul>
      )}
    </ProductNavDrawer>
  )
}

function tabRows(tab: SidebarTab, user: User): DrawerPage[] {
  if (tab === SidebarTab.Insurance) return insuranceDrawerPages(user.sidebar.insurance)
  if (tab === SidebarTab.Compliance) return user.sidebar.vault
  if (tab === SidebarTab.Finance) {
    return user.sidebar.finance.map((row) => ({ ...row, url: bookkeeperHref(row.url) }))
  }
  return []
}
